import {
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  flowMax,
} from 'ad-hok'
import { addEffectOnMount } from 'ad-hok-utils'
import cx from 'classnames'
import React, { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import { ProductWithImage, User } from 'shared/src/api/types'
import { addNavigate } from 'shared/src/utils/addNavigate'
import {
  addDataLoadingFromPromise,
  hasData,
  isLoaded,
} from 'shared/src/utils/dataLoading'

import { Product } from '../rodeo/Product'
import { addLaunchContext } from 'utils/launchContext'
import { addTranslationHelpers } from '../../../../utils/addTranslationHelpers'
import { serifFontFamily } from '../../../../utils/styles'
import { addMenuPage } from '../../../menu'
import { addRegisterPage } from '../../../register'
import { makeProductDetailUrl } from '../../../../utils/routes'

const useStyles = createUseStyles({
  featuredProductsTitle: {
    fontFamily: serifFontFamily,
    fontWeight: 500,
    fontSize: 18,
    color: 'white',
    marginTop: 0,
  },
  featuredProductsLasso: {
    marginBottom: 50,
  },
  featuredProductsList: {
    display: 'flex',
    gap: 11,
    overflowX: 'scroll',
    marginLeft: 24,
    marginBottom: 16,
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  lassoTitle: {
    margin: 0,
    marginBottom: 16,
    marginLeft: 24,
  },
  affiliateDisclosure: {
    fontFamily: 'sans-serif',
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 400,
    marginTop: 10,
    marginLeft: 26,
  },
})

const isGoogleCrawler = /Googlebot|GoogleOther|Google-InspectionTool/.test(
  navigator.userAgent
)

interface Props {
  productId: number
  user: User | null
}
export const RelatedProducts: FC<Props> = flowMax(
  addDisplayName('RelatedProducts'),
  addApi,
  addLaunchContext,
  addState(
    'hasSeenInitialRecommendations',
    'setSeenInitialRecommendations',
    false
  ),
  addEffectOnMount(({ setSeenInitialRecommendations }) => () => {
    setSeenInitialRecommendations(true)
  }),
  addDataLoadingFromPromise(
    'relatedProducts',
    ({ api, hasSeenInitialRecommendations, productId }) => {
      if (isGoogleCrawler) {
        return new Promise<Array<ProductWithImage>>((resolve) =>
          resolve(new Array<ProductWithImage>())
        )
      } else {
        return api.products.detailsRelatedProducts(productId, {
          random: hasSeenInitialRecommendations,
        })
      }
    },
    ['productId'],
    ({ productId }) => `relatedProducts-${productId}`
  ),
  addTranslationHelpers,
  addNavigate,
  addMenuPage,
  addRegisterPage,
  addState('isShuffling', 'setShuffling', false),
  addHandlers({
    onShuffle:
      ({ reloadRelatedProducts, setShuffling }) =>
      () => {
        setShuffling(true)
        reloadRelatedProducts()
          .catch()
          .then(() => {
            setShuffling(false)
          })
      },
  }),
  addProps(({ relatedProducts }) => ({
    hasRelatedProducts:
      hasData(relatedProducts) && relatedProducts.data.length > 0,
  })),
  addProps(({ user }) => ({
    isStealthMode: user?.isStealth,
  })),
  addProps(({ hasRelatedProducts, onShuffle, isShuffling }) => ({
    shuffle: hasRelatedProducts
      ? {
          onClick: onShuffle,
          disabled: isShuffling,
        }
      : undefined,
  })),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  ({ relatedProducts, hasRelatedProducts, isStealthMode, classes, t }) => (
    <>
      {isLoaded(relatedProducts) && hasRelatedProducts && !isStealthMode && (
        <>
          <p className={cx(classes.lassoTitle, classes.featuredProductsTitle)}>
            {t('productDetail.relatedProducts')}
          </p>
          <div className={classes.featuredProductsList}>
            {relatedProducts.data.map((product) => (
              <React.Fragment key={product.id}>
                <Product
                  product={product}
                  discoverySource="moreFromBrand"
                  navigateTo={makeProductDetailUrl(product.id)}
                  showBookmark
                  useGradient
                  showViews={false}
                />
              </React.Fragment>
            ))}
          </div>
        </>
      )}
      <div className={classes.affiliateDisclosure}>
        {t('affiliateDisclosure')}
      </div>
    </>
  )
)
